import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, MapPinned, History, Tag,
  BarChart3, User, LogOut, Menu, X, Wine, Users,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true, roles: "*" },
  { to: "/establishments", label: "Establishments", icon: Building2, testid: "nav-establishments", roles: "*" },
  { to: "/search", label: "Discover", icon: MapPinned, testid: "nav-search", roles: "*" },
  { to: "/visits", label: "Visits", icon: History, testid: "nav-visits", roles: "*" },
  { to: "/brands", label: "Brands", icon: Tag, testid: "nav-brands", roles: "*" },
  { to: "/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics", roles: "*" },
  { to: "/team", label: "Team", icon: Users, testid: "nav-team", roles: ["admin", "brand", "manager"] },
];

function NavItem({ item, onClick }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClick}
      data-testid={item.testid}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
          isActive
            ? "bg-brand-ink text-white shadow-soft"
            : "text-brand-ink2 hover:bg-brand-muted"
        }`
      }
    >
      <Icon size={18} strokeWidth={1.75} />
      <span>{item.label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink2">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-brand-bg/85 border-b border-brand-border">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden btn-ghost"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div className="flex items-center gap-2">
              <img
                src="/literally-logo.png"
                alt="Literally Vodka"
                className="w-10 h-10 rounded-lg object-cover shadow-soft"
              />
              <div className="leading-tight">
                <div className="font-display text-base text-brand-ink">LiterallyDrink</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-brand-mutedFg">Sales Tracker</div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <NavLink to="/profile" data-testid="nav-profile" className="btn-ghost gap-2">
              <User size={16} />
              <span className="hidden sm:inline text-sm font-medium">{user?.name || "Profile"}</span>
            </NavLink>
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="btn-ghost gap-2"
              title="Sign out"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline text-sm">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 grid md:grid-cols-[240px,1fr] gap-6 py-6">
        {/* Sidebar */}
        <aside className={`${open ? "block" : "hidden"} md:block`}>
          <nav className="card p-2 sticky top-24">
            <div className="px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-brand-mutedFg">Workspace</div>
            <div className="flex flex-col gap-1">
              {NAV.filter((item) => item.roles === "*" || (user && item.roles.includes(user.role))).map((item) => (
                <NavItem key={item.to} item={item} onClick={() => setOpen(false)} />
              ))}
            </div>
            <div className="px-3 pt-4 pb-2 text-[10px] uppercase tracking-[0.18em] text-brand-mutedFg">Account</div>
            <NavItem
              item={{ to: "/profile", label: "Profile", icon: User, testid: "nav-profile-side" }}
              onClick={() => setOpen(false)}
            />
          </nav>
        </aside>

        {/* Main */}
        <main className="min-w-0 fade-in">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
